import React, { useState } from 'react';
import Select from 'react-select';
import { SingleDatePicker } from 'react-dates';
import moment from 'moment';
import WpIcon from 'Wpds/components/icon/WpIcon';
import WpButton from 'Wpds/components/button/WpButton';
import WpButtonGroup from 'Wpds/components/button/WpButtonGroup';

const operatorOptions = option => {
  if (!option) {
    return [];
  }

  if (option.type === 'picklist') {
    return [
      { label: 'is equal to', value: 'IsEq' },
      { label: 'is one of', value: 'IsOneOf' },
      { label: 'is not one of', value: 'IsNotOneOf' },
      { label: 'has no change in', value: 'NoChangeIn' },
      { label: 'is null', value: 'IsNull' },
    ];
  }

  if (option.type === 'multipicklist') {
    return [
      { label: 'is null', value: 'IsNull' },
      { label: 'is not null', value: 'IsNotNull' },
    ];
  }

  if (option.type === 'datetime') {
    return [
      { label: 'is before', value: 'IsLessThan' },
      { label: 'is after', value: 'IsGreaterThan' },
      { label: 'is in the future by', value: 'IsInFutureBy' },
      { label: 'is in the past by', value: 'IsInPastBy' },
      { label: 'is null', value: 'IsNull' },
    ];
  }

  if (option.type === 'boolean') {
    return [
      { label: 'is true', value: 'IsTrue' },
      { label: 'is false', value: 'IsFalse' },
    ];
  }

  if (option.type === 'string') {
    return [
      { label: 'is equal to', value: 'IsEqForString' },
      { label: 'contains', value: 'ContainsForString' },
    ];
  }

  if (option.type === 'double') {
    return [{ label: 'is equal to', value: 'IsEqForDouble' }];
  }

  return [];
};

const valueOptions = ({ property, operator }) => {
  if (!property || !operator) {
    return [];
  }

  if (property.type === 'datetime' || operator.value === 'NoChangeIn') {
    return Array.from(Array(365)).map((_, i) => ({
      label: `${i + 1} day${i > 0 ? 's' : ''}`,
      value: `${i + 1}.Days`,
    }));
  }

  if (property.type === 'picklist') {
    return property.picklistValues;
  }

  if (property.type === 'boolean') {
    return [
      { label: 'True', value: 'True' },
      { label: 'False', value: 'False' },
    ];
  }

  return [];
};

const sanitizeValue = ({ property, operator, filter }) => {
  if (!property || !operator) return null;

  if (property.type === 'datetime' && (operator.value === 'IsLessThan' || operator.value === 'IsGreaterThan')) {
    if (moment.isMoment(filter.value)) return filter.value;
    const dateFromString = moment(filter.value);
    if (dateFromString.isValid()) return dateFromString;
    return null;
  } else if (property.type === 'string') {
    if (typeof filter.value === 'string' || filter.value instanceof String) return filter.value;
    return null;
  } else if (property.type === 'double') {
    if (typeof filter.value === 'number') return filter.value;
    if (typeof filter.value === 'string' || filter.value instanceof String) {
      const numberFromString = Number(filter.value);
      if (!Number.isNaN(numberFromString)) return numberFromString;
    }
    return null;
  } else {
    return valueOptions({ property, operator }).find(o => o.value === filter.value);
  }
};

const styles = value => ({
  container: base => ({
    ...base,
    width: value ? `${8 * value.length + 75}px` : '200px',
  }),
  menu: base => ({
    ...base,
    width: '400px',
  }),
});

const multiselect_styles = value => ({
  container: base => ({
    ...base,
    width: value ? `${Math.min(8 * value + 120, 500)}px` : '200px',
  }),
  menu: base => ({
    ...base,
    width: '400px',
  }),
});

type Props = {
  propertyOptions;
  onAddFilter;
  childFilters;
  uuid;
  onRemoveFilter;
  parentUuid;
  isFirst;
  onChange;
  joinType;
  toggleJoinType;
  filter;
};

const Filter = ({
  propertyOptions,
  onAddFilter,
  childFilters,
  uuid,
  onRemoveFilter,
  parentUuid,
  isFirst,
  onChange,
  joinType,
  toggleJoinType,
  filter,
}: Props) => {
  const propertyCategory = filter.property
    ? propertyOptions.find(o => o.label === filter.property.split('.')[0])
    : null;
  const property = propertyCategory ? propertyCategory.options.find(o => o.value === filter.property) : null;
  const operator = operatorOptions(property).find(o => o.value === filter.operator);
  const value = sanitizeValue({ property, operator, filter });
  const multiValue = valueOptions({ property, operator }).filter(
    o => Array.isArray(filter.value) && filter.value.includes(o.value)
  );

  const isDoubleInput = operator?.value === 'IsEqForDouble';
  const isTextInput = operator?.value === 'IsEqForString' || operator?.value === 'ContainsForString';
  const isDatePicker = operator?.value === 'IsLessThan' || operator?.value === 'IsGreaterThan';
  const isMultiSelect = operator?.value === 'IsOneOf' || operator?.value === 'IsNotOneOf';
  const isValueRequired = !(
    operator?.value === 'IsTrue' ||
    operator?.value === 'IsFalse' ||
    operator?.value === 'IsNull' ||
    operator?.value === 'IsNotNull'
  );
  const isGrouping = childFilters.length > 0;
  const isSingleSelect = !isMultiSelect && !isDatePicker && !isTextInput && !isDoubleInput;
  const [datePickerFocused, setDatePickerFocused] = useState(false);

  return (
    <>
      {(!isFirst || parentUuid) && (
        <div className="d-flex">
          <div className="join-type">{joinType}</div>
        </div>
      )}
      <div className={`mb-2 filter-container ${isGrouping ? 'group' : ''}`}>
        <div className="d-flex align-items-center filter">
          <Select
            className={`property mr-2 ${!property ? 'incomplete' : ''}`}
            placeholder="Property"
            options={propertyOptions}
            value={property}
            onChange={p => onChange({ uuid, property: p.value, operator: null, value: null }, property)}
            styles={styles(property?.label)}
          />
          <Select
            className={`operator mr-2 ${!operator ? 'incomplete' : ''}`}
            placeholder="Operator"
            options={operatorOptions(property)}
            value={operator}
            onChange={o => onChange({ ...filter, operator: o.value }, property)}
            styles={styles(operator?.label)}
          />
          {isValueRequired && isDatePicker && (
            <div className="mr-2">
              <SingleDatePicker
                id="startsAtInput"
                numberOfMonths={1}
                date={value}
                focused={datePickerFocused}
                onDateChange={v => onChange({ ...filter, value: v }, property)}
                onFocusChange={({ focused }) => setDatePickerFocused(focused)}
                isOutsideRange={() => false}
                inputIconPosition="after"
                hideKeyboardShortcutsPanel
                showClearDate
              />
            </div>
          )}
          {isValueRequired && isDoubleInput && (
            <div className="mr-2">
              <input
                type="number"
                value={value}
                onChange={event => onChange({ ...filter, value: event.target.value }, property)}
                step="0.1"
              />
            </div>
          )}
          {isValueRequired && isTextInput && (
            <div className="mr-2">
              <input
                type="text"
                value={value}
                onChange={event => onChange({ ...filter, value: event.target.value }, property)}
                data-isnull
              />
            </div>
          )}
          {isValueRequired && isSingleSelect && (
            <Select
              className={`value mr-3 ${!value ? 'incomplete' : ''}`}
              placeholder="Value"
              options={valueOptions({ property, operator })}
              value={value}
              onChange={v => onChange({ ...filter, value: v.value }, property)}
              styles={styles(value?.label)}
            />
          )}
          {isValueRequired && isMultiSelect && (
            <Select
              className={`value mr-3 ${!multiValue.length ? 'incomplete' : ''}`}
              placeholder="Value"
              options={valueOptions({ property, operator })}
              value={multiValue}
              isMulti
              onChange={v => onChange({ ...filter, value: v.map(v_element => v_element.value) }, property)}
              styles={multiselect_styles(multiValue.reduce((a, b) => a + b.label.length, 0))}
            />
          )}

          <WpButtonGroup spacing="sm">
            <WpButton variant="tertiary" size="sm" onClick={() => onAddFilter(uuid)}>
              <WpIcon name="plus-circle" />
            </WpButton>
            <WpButton variant="tertiary" size="sm" onClick={() => onRemoveFilter(uuid)}>
              <WpIcon name="times" />
            </WpButton>
          </WpButtonGroup>
        </div>

        <div className="child-filters mt-2">
          {childFilters.map((childFilter, index) => (
            <Filter
              propertyOptions={propertyOptions}
              onAddFilter={() => onAddFilter(uuid)}
              onRemoveFilter={onRemoveFilter}
              filter={childFilter}
              childFilters={[]} // no children of children for now
              uuid={childFilter.uuid}
              parentUuid={childFilter.parentUuid}
              key={childFilter.uuid}
              isFirst={index === 0}
              onChange={onChange}
              joinType={childFilter.joinType}
              toggleJoinType={toggleJoinType}
            />
          ))}
        </div>
        {isGrouping && (
          <div
            className="d-flex align-items-center toggle-join mb-2"
            onClick={() => toggleJoinType(childFilters[0]?.parentUuid)}
          >
            AND
            <span className="mx-2">
              <WpIcon name={childFilters[0]?.joinType === 'OR' ? 'toggle-on' : 'toggle-off'} />
            </span>
            OR
          </div>
        )}
      </div>
    </>
  );
};

export default Filter;
